import React from 'react';
import { DropMenuReact, PositionParent } from 'common/components/drop-menu';
import { Directions } from 'common/components/drop-menu/types';
import { SvgIconReact } from 'common/components/svg-icon';
import { TooltipReact } from 'common/components/tooltip';
import { IconSizeType } from 'common/constants';
import { useTranslation } from 'common/i18n/hooks';
import { getProfilePath } from 'common/report/util/profile/utils';
import { withStyles } from 'common/util/react';
import { react2panel } from 'common/util/react2panel';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize } from 'common/components/spinner/types';
import { TrackedUserIconReact } from '../tracked-user-icon';
import { RecordingMetadata, RecordingPageViewEventMetadata } from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
type ReplayPlaylistRowContextMenuProps = {
  contextPanelOpen: boolean;
  sortedReplays: RecordingMetadata[];
  currentRecordingIdx: number;
  replay: RecordingMetadata;
  replayPageViewEventsLoaded: boolean;
  replayPageViewEvents: RecordingPageViewEventMetadata[];
  displayName: string;
  positionParent: PositionParent;
  onContextMenuHover: (currentIdx: number | null) => void;
};
function replayPlaylistRowContextMenu(props: ReplayPlaylistRowContextMenuProps) {
  var _props$sortedReplays$, _props$replay, _props$replay2;
  const {
    t
  } = useTranslation();
  const isReplaySelected = props.replay.replayId === ((_props$sortedReplays$ = props.sortedReplays[props.currentRecordingIdx]) === null || _props$sortedReplays$ === void 0 ? void 0 : _props$sortedReplays$.replayId);
  const getOSLogoToShow = () => {
    switch (props.replay.os) {
      case "Mac OS X":
      case "iOS":
        return "apple-logo";
      case "Windows":
      case "Windows Phone":
        return "windows-logo";
      case "Ubuntu":
        return "ubuntu-logo";
      case "Android":
        return "android-logo";
      default:
        return "os";
    }
  };
  const getBrowserLogoToShow = () => {
    var _props$replay$browser;
    const browserLowerCase = ((_props$replay$browser = props.replay.browser) === null || _props$replay$browser === void 0 ? void 0 : _props$replay$browser.toLowerCase()) || "";
    if (browserLowerCase.includes("chrome")) {
      return "chrome-logo";
    } else if (browserLowerCase.includes("firefox")) {
      return "firefox-logo";
    } else if (browserLowerCase.includes("safari")) {
      return "safari-logo";
    } else if (browserLowerCase.includes("edge")) {
      return "edge-logo";
    } else if (browserLowerCase.includes("opera")) {
      return "opera-logo";
    } else if (browserLowerCase.includes("brave")) {
      return "brave-logo";
    }
    return "browser";
  };
  const startingURL = props.replay.startingURL || t("session-replay.unknown-starting-url");
  const exitURL = props.replayPageViewEvents.length > 0 ? [...props.replayPageViewEvents].sort((a, b) => a.eventTime - b.eventTime)[props.replayPageViewEvents.length - 1].currentURL : t("session-replay.unknown-exit-url");
  const currentIdx = props.sortedReplays.findIndex(replay => replay.replayId === props.replay.replayId);
  return <div className={styles.replaysListItemContextMenuContainer} onMouseOver={() => props.onContextMenuHover(currentIdx)} onMouseOut={() => props.onContextMenuHover(null)} data-sentry-component="replayPlaylistRowContextMenu" data-sentry-source-file="playlist-row-context-menu.tsx">
      <DropMenuReact open={props.contextPanelOpen} offset={0} direction={Directions.Right} positionParent={props.positionParent} data-sentry-element="DropMenuReact" data-sentry-source-file="playlist-row-context-menu.tsx">
        <div className={styles.replaysListItemContextMenu}>
          <div className={styles.replaysListItemContext}>
            <TrackedUserIconReact className={styles.replaysListItemUserIcon} distinctId={props.replay.distinctId} imgSrc={props.replay.avatarUrl} email={props.replay.email} size={IconSizeType.Medium} enableOutline={isReplaySelected} data-sentry-element="TrackedUserIconReact" data-sentry-source-file="playlist-row-context-menu.tsx" />
            <div className={styles.replaysListItemText}>
              <div className={styles.replaysListItemTextTitle}>{props.displayName}</div>
              <div className={styles.replaysListItemTextSubtitle}>{props.replay.email}</div>
            </div>
            <a href={getProfilePath(props.replay.distinctId, null)} target='_blank'>
              <SvgIconReact className={styles.replaysListItemIcon} icon={"link-out"} data-sentry-element="SvgIconReact" data-sentry-source-file="playlist-row-context-menu.tsx" />
            </a>
          </div>
          <div className={styles.replaysListItemContextIcons}>
            <div className={styles.replaysListItemContextIconContainer}>
              <SvgIconReact className={styles.replaysListItemContextIcon} icon={props.replay.source === "web" ? "laptop" : "mobile"} data-sentry-element="SvgIconReact" data-sentry-source-file="playlist-row-context-menu.tsx" />
              <TooltipReact text={"Source SDK: ".concat(props.replay.source || t("session-replay.unknown-source"))} data-sentry-element="TooltipReact" data-sentry-source-file="playlist-row-context-menu.tsx" />
            </div>
            <div className={styles.replaysListItemContextIconContainer}>
              <SvgIconReact className={styles.replaysListItemContextIcon} icon={getOSLogoToShow()} data-sentry-element="SvgIconReact" data-sentry-source-file="playlist-row-context-menu.tsx" />
              <TooltipReact text={"Operating System: ".concat(props.replay.os || t("session-replay.unknown-os"))} data-sentry-element="TooltipReact" data-sentry-source-file="playlist-row-context-menu.tsx" />
            </div>
            {props.replay.source === "web" && <div className={styles.replaysListItemContextIconContainer}>
                <SvgIconReact className={styles.replaysListItemContextIcon} icon={getBrowserLogoToShow()} />
                <TooltipReact text={"Browser: ".concat(props.replay.browser || t("session-replay.unknown-browser"))} />
              </div>}
          </div>
          {((_props$replay = props.replay) === null || _props$replay === void 0 ? void 0 : _props$replay.source) === "web" && <div className={styles.replaysListItemContextUrl}>
              <div className={styles.replaysListItemContextUrlText}>{t("session-replay.starting-url")}</div>
              <div className={styles.replaysListItemContextUrlText}>
                {startingURL}
                <TooltipReact text={startingURL} />
              </div>
            </div>}
          {((_props$replay2 = props.replay) === null || _props$replay2 === void 0 ? void 0 : _props$replay2.source) === "web" && <div className={styles.replaysListItemContextUrl}>
              <div className={styles.replaysListItemContextUrlText}>{t("session-replay.exit-url")}</div>
              {!props.replayPageViewEventsLoaded ? <SpinnerReact className={styles.replaysListItemContextUrlSpinner} size={SpinnerSize.Small} /> : <div className={styles.replaysListItemContextUrlText}>
                  {exitURL}
                  <TooltipReact text={exitURL} />
                </div>}
            </div>}
        </div>
      </DropMenuReact>
    </div>;
}
export const ReplayPlaylistRowContextMenuReact = withStyles(React.forwardRef(replayPlaylistRowContextMenu), css);
export const ReplayPlaylistRowContextMenuEl = react2panel("mp-replay-playlist-row", ReplayPlaylistRowContextMenuReact);